/**
 * Button Component
 * Material Design 3 Button implementation for React/MUI
 */

import React, { useCallback } from 'react';
import { CSS_CLASSES, DEFAULTS } from './constants';
import { usePressState, useToggleState, useBorderRadius } from './hooks';
import { StyledButton } from './Button.styled';
import type { ButtonProps, ButtonShape, ButtonSize, ButtonVariant, SmallPadding } from './types';

// Re-export types for convenience
export type { ButtonProps, ButtonShape, ButtonSize, ButtonVariant, SmallPadding };

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Button - Material 3 Button Component
 *
 * A comprehensive button component implementing Material Design 3 specifications
 * with support for multiple sizes, variants, shapes, and toggle functionality.
 *
 * @example
 * // Basic usage
 * <Button variant="filled" size="sm">Click me</Button>
 *
 * @example
 * // Toggle button
 * <Button toggle selected={isSelected} onSelectedChange={setIsSelected}>
 *   Toggle
 * </Button>
 *
 * @example
 * // With icon and shape morphing
 * <Button
 *   variant="tonal"
 *   size="md"
 *   startIcon={<AddIcon />}
 *   morphShape
 * >
 *   Add Item
 * </Button>
 */
export const Button = ({
  borderRadius: customBorderRadius,
  children,
  className,
  disabled,
  endIcon,
  morphShape = DEFAULTS.morphShape,
  onClick,
  onSelectedChange,
  ref,
  selected: controlledSelected,
  shape = DEFAULTS.shape as ButtonShape,
  size = DEFAULTS.size as ButtonSize,
  smallPadding = DEFAULTS.smallPadding as SmallPadding,
  startIcon,
  testId = 'button',
  toggle = DEFAULTS.toggle,
  variant = DEFAULTS.variant as ButtonVariant,
  ...rest
}: ButtonProps) => {
  // Use custom hooks for state management
  const { handleToggle, isSelected } = useToggleState({
    onSelectedChange,
    selected: controlledSelected,
    toggle,
  });

  const { isPressed, pressHandlers } = usePressState();

  const borderRadius = useBorderRadius({
    customBorderRadius,
    isPressed,
    isSelected,
    morphShape,
    shape,
    size,
  });

  // Handle click with toggle support
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      handleToggle();
      onClick?.(event);
    },
    [handleToggle, onClick],
  );

  // Build class name
  const buttonClassName = [
    CSS_CLASSES.root,
    isSelected && CSS_CLASSES.selected,
    morphShape && CSS_CLASSES.morphing,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledButton
      ref={ref}
      aria-pressed={toggle ? isSelected : undefined}
      aria-disabled={disabled}
      className={buttonClassName}
      data-testid={testId}
      disabled={disabled}
      onClick={handleClick}
      $borderRadius={borderRadius}
      $morphShape={morphShape}
      $size={size}
      $smallPadding={smallPadding}
      $variant={variant}
      $selected={isSelected}
      {...pressHandlers}
      {...rest}
    >
      {startIcon && <span className={CSS_CLASSES.startIcon}>{startIcon}</span>}
      {children}
      {endIcon && <span className={CSS_CLASSES.endIcon}>{endIcon}</span>}
    </StyledButton>
  );
};

Button.displayName = 'Button';

export default Button;
